import { createContext, useContext, useEffect, useState, useRef, ReactNode, useCallback } from 'react';
import { lovable } from '@/integrations/lovable/index';
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from '@capacitor/push-notifications';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import type { Database } from '@/integrations/supabase/types';
import { PUSH_CONFIG } from '@/config/push';
import { identifyUser, resetUser } from '@/lib/posthog';
import { trackSignupCompletedOnce } from '@/lib/analytics';

import { queryClient } from '@/lib/query-client';
import { recordRevalidation } from '@/lib/subscription-metrics';
import { NEGATIVE_CHECKS_TO_BLOCK } from '@/lib/subscription-rules';

import { useNavigate } from 'react-router-dom';


type AppRole = Database['public']['Enums']['app_role'];

// Helper to convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer;
}

// Key for storing pending invitation in sessionStorage (exported from AcceptInvite)
const PENDING_INVITE_KEY = 'careos_pending_invite';
const PENDING_COMPANY_INVITE_KEY = 'careos_pending_company_invite';

export type PaymentStatus = 'ok' | 'past_due_grace' | 'past_due_expired';

interface SubscriptionInfo {
  subscribed: boolean;
  productId: string | null;
  productName: string | null;
  priceId: string | null;
  unitAmount: number | null;
  currency: string | null;
  quantity: number | null;
  interval: string | null;
  intervalCount: number | null;
  subscriptionEnd: string | null;
  patientSlots: number | null;
  isCompanyPlan: boolean;
  paymentStatus: PaymentStatus;
  gracePeriodEnd: string | null;
  paymentFailedAt: string | null;
}

const EMPTY_SUBSCRIPTION: SubscriptionInfo = {
  subscribed: false,
  productId: null,
  productName: null,
  priceId: null,
  unitAmount: null,
  currency: null,
  quantity: null,
  interval: null,
  intervalCount: null,
  subscriptionEnd: null,
  patientSlots: null,
  isCompanyPlan: false,
  paymentStatus: 'ok',
  gracePeriodEnd: null,
  paymentFailedAt: null,
};

/**
 * Last-known-good subscription snapshot.
 *
 * After long idle periods the app can momentarily lose the access token (silent
 * refresh) or get a slow/cold `check-subscription` response. Without a cached
 * snapshot the guard sees `subscribed: false` and bounces the user to
 * /app/subscription even though the plan is perfectly active. We persist the
 * last successful *positive* result and rehydrate it on boot so guards always
 * start from the last truthful state.
 */
const SUB_SNAPSHOT_KEY = 'careos_sub_snapshot';
const SUB_SNAPSHOT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function loadSubscriptionSnapshot(): SubscriptionInfo {
  try {
    const raw = localStorage.getItem(SUB_SNAPSHOT_KEY);
    if (!raw) return EMPTY_SUBSCRIPTION;
    const parsed = JSON.parse(raw) as { savedAt?: number; data?: SubscriptionInfo };
    if (!parsed?.data || !parsed.savedAt) return EMPTY_SUBSCRIPTION;
    if (Date.now() - parsed.savedAt > SUB_SNAPSHOT_MAX_AGE_MS) return EMPTY_SUBSCRIPTION;
    return { ...EMPTY_SUBSCRIPTION, ...parsed.data };
  } catch {
    return EMPTY_SUBSCRIPTION;
  }
}

function saveSubscriptionSnapshot(data: SubscriptionInfo) {
  try {
    if (data.subscribed) {
      localStorage.setItem(SUB_SNAPSHOT_KEY, JSON.stringify({ savedAt: Date.now(), data }));
    } else {
      localStorage.removeItem(SUB_SNAPSHOT_KEY);
    }
  } catch {
    // storage unavailable — snapshot is best-effort
  }
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  roles: AppRole[];
  subscription: SubscriptionInfo;
  isCheckingSubscription: boolean;
  hasCheckedSubscription: boolean;
  isRevalidatingSubscription: boolean;
  /** Consecutive successful checks that answered "not subscribed". */
  consecutiveNegativeChecks: number;
  isLoggingOut: boolean;

  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: (returnTo?: string) => Promise<{ error: Error | null }>;
  signInWithApple: (returnTo?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  hasRole: (role: AppRole) => boolean;
  checkSubscription: (silent?: boolean) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionInfo>(() =>
    typeof window !== 'undefined' ? loadSubscriptionSnapshot() : EMPTY_SUBSCRIPTION
  );
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [hasCheckedSubscription, setHasCheckedSubscription] = useState(false);
  const [isRevalidatingSubscription, setIsRevalidatingSubscription] = useState(false);
  const [consecutiveNegativeChecks, setConsecutiveNegativeChecks] = useState(0);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Consecutive "not subscribed" answers (ref mirrors state for sync reads).
  const negativeChecksRef = useRef(0);


  // Track consecutive auth failures to decide whether to force logout vs. wait for refresh
  const authFailureCountRef = useRef(0);
  // Track first-load state per session token to apply downgrade-retry on initial check too
  const initialCheckDoneRef = useRef(false);
  // Timer for clearing the post-wake grace window
  const revalidationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Number of subscription checks currently in flight (including silent ones).
  // While > 0 the guard must not redirect: the answer is still unknown.
  const inFlightChecksRef = useRef(0);

  // Ref to track if GoogleAuth plugin was used in this session (for safe cleanup)
  const googleAuthUsedRef = useRef(false);

  const navigate = useNavigate();

  const checkSubscription = useCallback(async (silent: boolean = false) => {
    if (!session?.access_token || isLoggingOut) {
      if (isLoggingOut) console.log('[AuthContext] Skipping checkSubscription because logout is in progress');
      // IMPORTANT: do NOT wipe the subscription just because the access token is
      // momentarily unavailable (silent token refresh after idle). Clearing it here
      // made the guard see subscribed=false and redirect to /app/subscription.
      // The real reset happens in the auth listener's "session cleared" branch.
      return;
    }

    if (!silent) {
      setIsCheckingSubscription(true);
    }

    // Keep the guard in "revalidating" state for the whole duration of the request,
    // even for silent checks (cold starts / Stripe latency can exceed the grace window).
    inFlightChecksRef.current += 1;
    setIsRevalidatingSubscription(true);

    let succeeded = false;

    try {
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('[AuthContext] Error invoking check-subscription function:', error);

        const errorDetails = error as any;
        // supabase-js FunctionsHttpError carries the Response on `context`.
        const status = errorDetails?.status ?? errorDetails?.context?.status;
        const message = errorDetails?.message ?? '';

        // 401: try refreshing the session FIRST. Only force logout after 2 consecutive failures.
        if (status === 401 || (typeof message === 'string' && message.includes('401'))) {
          authFailureCountRef.current += 1;
          console.warn('[AuthContext] 401 on check-subscription. Attempting session refresh.', { attempt: authFailureCountRef.current });
          try {
            const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
            if (!refreshErr && refreshed?.session) {
              console.log('[AuthContext] Session refresh succeeded; retrying check-subscription shortly');
              authFailureCountRef.current = 0;
              // Don't mark as checked yet — let the next effect run pick it up.
              return;
            }
            console.warn('[AuthContext] Session refresh failed:', refreshErr);
          } catch (e) {
            console.warn('[AuthContext] Session refresh threw:', e);
          }
          if (authFailureCountRef.current >= 2) {
            console.warn('[AuthContext] Multiple auth failures — forcing logout.');
            authFailureCountRef.current = 0;
            signOut();
          }
          return; // Do NOT mark hasCheckedSubscription so guard keeps showing loader.
        }

        // Treat 5xx / network as transient: keep prior state and DO NOT mark as checked.
        // Schedule a retry with backoff.
        const isTransient = !status || status >= 500 || (errorDetails?.context?.transient === true);
        if (isTransient) {
          console.warn('[AuthContext] Transient error on check-subscription — keeping prior state and retrying.');
          setTimeout(() => { checkSubscription(true); }, 3000);
          return;
        }
        return;
      }

      // Edge function may explicitly mark a transient response (HTTP 503 with body { transient: true })
      if (data && (data as any).transient === true) {
        console.warn('[AuthContext] check-subscription returned transient flag — keeping prior state and retrying.');
        setTimeout(() => { checkSubscription(true); }, 3000);
        return;
      }

      console.log('[AuthContext] Subscription check result:', data);
      authFailureCountRef.current = 0;
      succeeded = true;

      const newSubscribed = data?.subscribed ?? false;

      // Count consecutive negative answers. The paywall only triggers after
      // NEGATIVE_CHECKS_TO_BLOCK of them, so a single false negative (cold
      // start, Stripe race, token refresh) can never bounce the user out.
      if (newSubscribed) {
        negativeChecksRef.current = 0;
        setConsecutiveNegativeChecks(0);
      } else {
        negativeChecksRef.current += 1;
        setConsecutiveNegativeChecks(negativeChecksRef.current);
        if (negativeChecksRef.current < NEGATIVE_CHECKS_TO_BLOCK) {
          // Get the confirming (or contradicting) second data point quickly.
          setTimeout(() => { checkSubscription(true); }, 1500);
        }
      }

      initialCheckDoneRef.current = true;
      const next: SubscriptionInfo = {
        subscribed: newSubscribed,
        productId: data?.product_id ?? null,
        productName: data?.product_name ?? null,
        priceId: data?.price_id ?? null,
        unitAmount: data?.unit_amount ?? null,
        currency: data?.currency ?? null,
        quantity: data?.quantity ?? null,
        interval: data?.interval ?? null,
        intervalCount: data?.interval_count ?? null,
        subscriptionEnd: data?.subscription_end ?? null,
        patientSlots: data?.patient_slots ?? null,
        isCompanyPlan: data?.is_company_plan ?? false,
        paymentStatus: (data?.payment_status ?? 'ok') as PaymentStatus,
        gracePeriodEnd: data?.grace_period_end ?? null,
        paymentFailedAt: data?.payment_failed_at ?? null,
      };
      saveSubscriptionSnapshot(next);
      setSubscription(next);

    } catch (err) {
      console.error('Error checking subscription:', err);
      // Network throw → treat as transient
      setTimeout(() => { checkSubscription(true); }, 3000);
    } finally {
      if (!silent) {
        setIsCheckingSubscription(false);
      }
      inFlightChecksRef.current = Math.max(0, inFlightChecksRef.current - 1);
      if (inFlightChecksRef.current === 0) {
        // Small tail so React can settle the new subscription state before guards
        // are allowed to make a redirect decision again.
        if (revalidationTimerRef.current) clearTimeout(revalidationTimerRef.current);
        revalidationTimerRef.current = setTimeout(() => {
          if (inFlightChecksRef.current === 0) setIsRevalidatingSubscription(false);
        }, 1500);
      }
      // Only mark as checked when we got a real, non-transient answer.
      if (succeeded) {
        setHasCheckedSubscription(true);
      }
    }
  }, [session?.access_token, isLoggingOut]);

  useEffect(() => {
    // Helper to sync Google profile data
    const syncGoogleProfile = async (syncUser: User) => {
      // Only proceed if we have metadata
      const { full_name, avatar_url } = syncUser.user_metadata;
      if (!full_name && !avatar_url) return;

      try {
        // Check if profile exists and has data
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name, avatar_url')
          .eq('user_id', syncUser.id)
          .single();

        // Only update if profile is missing info that we have from Google
        if (profile) {
          const updates: any = {};
          if (!profile.full_name && full_name) updates.full_name = full_name;
          if (!profile.avatar_url && avatar_url) updates.avatar_url = avatar_url;

          if (Object.keys(updates).length > 0) {
            await supabase
              .from('profiles')
              .update(updates)
              .eq('user_id', syncUser.id);
          }
        } else {
          // Profile doesn't exist
          await supabase
            .from('profiles')
            .upsert({
              user_id: syncUser.id,
              full_name: full_name || '',
              avatar_url: avatar_url || null,
              updated_at: new Date().toISOString()
            });
        }
      } catch (err) {
        console.error('Error syncing Google profile:', err);
      }
    };

    // After SIGNED_IN, persist the referral code (captured by useReferralCapture
    // into localStorage 'careos_ref') onto the user's profile, but only once
    // (don't overwrite an existing attribution).
    const persistReferralAttribution = async (syncUser: User) => {
      try {
        const raw = localStorage.getItem('careos_ref');
        if (!raw) return;
        let code: string | null = null;
        try {
          const parsed = JSON.parse(raw) as { code?: string };
          code = parsed?.code ?? null;
        } catch {
          code = null;
        }
        if (!code) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('referred_by_code')
          .eq('user_id', syncUser.id)
          .maybeSingle();

        if (profile && !profile.referred_by_code) {
          await supabase
            .from('profiles')
            .update({ referred_by_code: code })
            .eq('user_id', syncUser.id);
        } else if (!profile) {
          await supabase
            .from('profiles')
            .upsert({ user_id: syncUser.id, referred_by_code: code, updated_at: new Date().toISOString() });
        }
      } catch (err) {
        console.error('Error persisting referral attribution:', err);
      }
    };

    // Set up auth state listener
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.log('[AuthContext] onAuthStateChange event:', event);

        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        setLoading(false);

        if (currentSession?.user) {
          // Identify user in PostHog
          identifyUser(currentSession.user.id, {
            email: currentSession.user.email,
            full_name: currentSession.user.user_metadata?.full_name,
          });

          // Catch-all signup tracking: covers Google/Apple/native flows that
          // never reached the email+OTP screen. Fires at most once per user.
          if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
            const pendingInvite = sessionStorage.getItem(PENDING_INVITE_KEY);
            const pendingCompanyInvite = sessionStorage.getItem(PENDING_COMPANY_INVITE_KEY);
            trackSignupCompletedOnce(currentSession.user, {
              audience: pendingCompanyInvite ? 'company' : pendingInvite ? 'caregiver' : 'owner',
            });
          }


          fetchUserRoles(currentSession.user.id);
          syncGoogleProfile(currentSession.user);
          persistReferralAttribution(currentSession.user);
          // Auto-link an admin-created partner record (status=active, user_id=null)
          // to this signed-in user when emails match. Safe no-op otherwise.
          if (currentSession.user.email) {
            supabase.rpc('claim_referral_partner_for_user', {
              _user_id: currentSession.user.id,
              _email: currentSession.user.email,
            }).then(() => {}, () => {});
          }

          // Check for pending invite after login or redirect to /app if on landing page
          if (event === 'SIGNED_IN') {
            const pendingInvite = sessionStorage.getItem(PENDING_INVITE_KEY);
            const pendingCompanyInvite = sessionStorage.getItem(PENDING_COMPANY_INVITE_KEY);
            const currentPath = window.location.pathname;

            if (pendingCompanyInvite) {
              const targetPath = `/invite/company/${pendingCompanyInvite}`;
              sessionStorage.removeItem(PENDING_COMPANY_INVITE_KEY);
              if (currentPath !== targetPath) {
                navigate(targetPath, { replace: true });
              }
            } else if (pendingInvite) {
              const targetPath = `/invite/${pendingInvite}`;
              sessionStorage.removeItem(PENDING_INVITE_KEY);

              if (currentPath !== targetPath) {
                navigate(targetPath, { replace: true });
              }
            } else if (!Capacitor.isNativePlatform() && (currentPath === '/' || currentPath === '/auth' || currentPath === '/google-auth')) {
              // SPA navigation (no full reload) — avoids the white flash post-login
              console.log('[AuthContext] Landing on public page while signed in, redirecting to /app');
              navigate('/app', { replace: true });
            }
          }
        } else {
          // Session cleared
          setRoles([]);
          saveSubscriptionSnapshot(EMPTY_SUBSCRIPTION);
          setSubscription(EMPTY_SUBSCRIPTION);
          setHasCheckedSubscription(false);
          negativeChecksRef.current = 0;
          setConsecutiveNegativeChecks(0);

          resetUser();
          queryClient.clear();
        }
      }
    );

    // Initial session check
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      if (existingSession) {
        setSession(existingSession);
        setUser(existingSession.user);
        fetchUserRoles(existingSession.user.id);
        syncGoogleProfile(existingSession.user);
      }
      setLoading(false);
    });

    return () => {
      authSubscription.unsubscribe();
    };
  }, []);

  // Reset per-token first-check tracking when session token changes
  useEffect(() => {
    initialCheckDoneRef.current = false;
    authFailureCountRef.current = 0;
  }, [session?.access_token]);

  // Check subscription when session changes — silent so it doesn't block guards
  useEffect(() => {
    if (session?.access_token) {
      recordRevalidation('session-change');
      checkSubscription(true);
    }
  }, [session?.access_token, checkSubscription]);

  // Auto-refresh subscription every 60 seconds
  useEffect(() => {
    if (!session?.access_token) return;

    const interval = setInterval(() => {
      recordRevalidation('interval');
      checkSubscription(true); // Silent check
    }, 60000);

    return () => clearInterval(interval);
  }, [session?.access_token, checkSubscription]);

  // Revalidate subscription when the tab/app returns from background or network reconnects.
  // While revalidating, set a short grace window so the SubscriptionGuard does not redirect
  // based on stale state.
  useEffect(() => {
    if (!session?.access_token) return;

    const startGraceWindow = () => {
      setIsRevalidatingSubscription(true);
      if (revalidationTimerRef.current) clearTimeout(revalidationTimerRef.current);
      revalidationTimerRef.current = setTimeout(() => {
        // Never end the grace window while a check is still in flight.
        if (inFlightChecksRef.current === 0) setIsRevalidatingSubscription(false);
      }, 5000);
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        console.log('[AuthContext] Tab became visible — revalidating subscription');
        recordRevalidation('visibilitychange');
        startGraceWindow();
        checkSubscription(true);
      }
    };

    const handleOnline = () => {
      console.log('[AuthContext] Network back online — revalidating subscription');
      recordRevalidation('online');
      startGraceWindow();
      checkSubscription(true);
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
      if (revalidationTimerRef.current) {
        clearTimeout(revalidationTimerRef.current);
        revalidationTimerRef.current = null;
      }
    };
  }, [session?.access_token, checkSubscription]);

  const registerPushNotifications = useCallback(async () => {
    if (!user?.id) return;

    try {
      if (Capacitor.isNativePlatform()) {
        const { receive } = await FirebaseMessaging.requestPermissions();
        if (receive !== 'granted') return;

        try {
          const { token } = await FirebaseMessaging.getToken();
          if (!token) return;

          const currentUserAgent = window.navigator.userAgent;

          // Idempotency: only touch DB if this exact (user, token) pair isn't already registered
          const { data: existing } = await supabase
            .from('push_subscriptions')
            .select('id')
            .eq('user_id', user.id)
            .eq('subscription->>token', token)
            .limit(1);

          if (existing && existing.length > 0) {
            // Já registrado — apenas marca o dispositivo como ativo (evita
            // enviar push para aparelhos abandonados, que geram duplicidade).
            await supabase
              .from('push_subscriptions')
              .update({ last_seen_at: new Date().toISOString() })
              .eq('id', existing[0].id);
            return;
          }


          console.log('FCM registration success, token:', token);

          // Remove any prior registration of this exact token (e.g. another user on same device)
          await supabase
            .from('push_subscriptions')
            .delete()
            .eq('subscription->>token', token);

          // Remove any stale rows of this device for THIS user
          await supabase
            .from('push_subscriptions')
            .delete()
            .eq('user_id', user.id)
            .eq('user_agent', currentUserAgent);

          const { error } = await supabase
            .from('push_subscriptions')
            .insert({
              user_id: user.id,
              subscription: {
                token: token,
                platform: Capacitor.getPlatform(),
                type: 'fcm'
              },
              user_agent: currentUserAgent
            });

          if (error) {
            console.error('Error saving FCM token:', error.message);
          } else {
            console.log('FCM token persisted for user', user.id);
          }
        } catch (err) {
          console.error('Error getting FCM token:', err);
        }

      } else if ('serviceWorker' in navigator && 'PushManager' in window && Notification.permission === 'granted') {
        // Web Push Logic — only register if permission is ALREADY granted (no auto-prompt)
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(PUSH_CONFIG.VAPID_PUBLIC_KEY)
        });

        const subscriptionJson = subscription.toJSON();
        const currentUserAgent = window.navigator.userAgent;

        // Idempotency: skip if this user already has this endpoint registered
        if (subscriptionJson.endpoint) {
          const { data: existing } = await supabase
            .from('push_subscriptions')
            .select('id')
            .eq('user_id', user.id)
            .eq('subscription->>endpoint', subscriptionJson.endpoint)
            .limit(1);

          if (existing && existing.length > 0) {
            await supabase
              .from('push_subscriptions')
              .update({ last_seen_at: new Date().toISOString() })
              .eq('id', existing[0].id);
            return;
          }


          // Remove this endpoint registered under any other user (account switch on same device)
          await supabase
            .from('push_subscriptions')
            .delete()
            .eq('subscription->>endpoint', subscriptionJson.endpoint);
        }

        // Remove stale subscriptions for this device + user
        await supabase
          .from('push_subscriptions')
          .delete()
          .eq('user_id', user.id)
          .eq('user_agent', currentUserAgent);

        const { error } = await supabase
          .from('push_subscriptions')
          .insert({
            user_id: user.id,
            subscription: subscriptionJson as any,
            user_agent: currentUserAgent
          });

        if (error) {
          console.error('Error saving Web Push subscription:', error);
        } else {
          console.log('Web Push registered automatically for user:', user.id);
        }
      }

    } catch (error) {
      console.error('Error registering push notifications:', error);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user) {
      // Run once per user. Push registration is idempotent inside.
      registerPushNotifications();
    }
  }, [user?.id, registerPushNotifications]);


  const lastFetchedRolesUserIdRef = useRef<string | null>(null);

  const fetchUserRoles = async (userId: string) => {
    if (isLoggingOut) return;
    // Skip refetch when roles are already loaded for this user (e.g. TOKEN_REFRESHED).
    if (lastFetchedRolesUserIdRef.current === userId && roles.length > 0) return;
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (error) {
        console.error('Error fetching roles:', error);
        return;
      }

      if (data) {
        lastFetchedRolesUserIdRef.current = userId;
        setRoles(data.map(r => r.role));
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const redirectUrl = `${window.location.origin}/app`;
    // Trim surrounding whitespace so names/emails aren't stored with stray spaces.
    const cleanEmail = email.trim().toLowerCase();
    const cleanName = fullName?.replace(/\s+/g, ' ').trim();

    const { data, error } = await supabase.auth.signUp({
      email: cleanEmail,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: cleanName ? { full_name: cleanName } : undefined
      }
    });

    if (error) return { error: error as Error | null };

    // Supabase returns a user with empty identities when the email is already registered
    // (user_repeated_signup). Detect this and return a clear error.
    if (data?.user && (!data.user.identities || data.user.identities.length === 0)) {
      return { error: new Error('already registered') };
    }

    return { error: null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    return { error: error as Error | null };
  };

  const signInWithGoogle = async (returnTo?: string) => {
    try {
      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
        // iOS: fluxo web via SFSafariViewController para suporte correto ao nonce (PKCE)
        console.log('[Auth] Starting browser-based Google Sign-In for iOS');

        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: 'careos://google-auth',
            skipBrowserRedirect: true,
          },
        });

        if (error) throw error;
        if (!data.url) throw new Error('No OAuth URL returned from Supabase');

        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url: data.url, windowName: '_self' });

        // A sessão será estabelecida pelo handler appUrlOpen em App.tsx
        return { error: null };

      } else if (Capacitor.isNativePlatform()) {
        // Android: mantém o fluxo nativo com GoogleAuth plugin
        console.log('[Auth] Starting native Google Sign-In for Android');

        try {
          await GoogleAuth.initialize();
        } catch (e) {
          // Plugin already initialized or using config files
        }

        const googleUser = await GoogleAuth.signIn();

        if (!googleUser?.authentication?.idToken) {
          throw new Error('No ID Token received from Google');
        }

        const { error } = await supabase.auth.signInWithIdToken({
          provider: 'google',
          token: googleUser.authentication.idToken,
        });

        if (error) throw error;

        // Mark that GoogleAuth was used in this session (for cleanup on logout)
        googleAuthUsedRef.current = true;
        console.log('[Auth] Native Google Sign-In successful');
        return { error: null };

      } else {
        // Web: fluxo OAuth padrão com redirect
        const absoluteReturnTo = new URL(returnTo || '/app', window.location.origin).toString();
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: { redirectTo: absoluteReturnTo }
        });
        return { error: error as Error | null };
      }
    } catch (error: any) {
      console.error('[Auth] Google Sign-In error:', error);
      return { error: error as Error | null };
    }
  };

  const signInWithApple = async (returnTo?: string) => {
    try {
      const result = await lovable.auth.signInWithOAuth("apple", {
        redirect_uri: new URL(returnTo || '/app', window.location.origin).toString(),
      });

      if (result.error) {
        return { error: result.error as Error };
      }

      if (result.redirected) {
        return { error: null };
      }

      return { error: null };
    } catch (error: any) {
      console.error('[Auth] Apple Sign-In error:', error);
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);

    console.group('[Auth] Nuclear SignOut initiated');
    console.log('[Auth] Cleaning up assets...');

    // CRITICAL: Check if GoogleAuth was actually USED in this session, not just if user HAS google linked
    // This prevents crashes when user has both email/password and Google but logged in via email/password
    const shouldCleanupGoogleAuth = googleAuthUsedRef.current;
    console.log('[Auth] Should cleanup GoogleAuth:', shouldCleanupGoogleAuth);

    // 1. Cancel all pending queries immediately to avoid any background logic firing with old session
    try {
      await queryClient.cancelQueries();
      console.log('[Auth] Pending queries cancelled');
    } catch (e) {
      console.warn('[Auth] Error cancelling queries:', e);
    }

    // 2. Clear local state IMMEDIATELY
    setUser(null);
    setSession(null);
    setRoles([]);
    saveSubscriptionSnapshot(EMPTY_SUBSCRIPTION);
    setSubscription(EMPTY_SUBSCRIPTION);
    console.log('[Auth] Internal state cleared');

    // 3. Optional cleanups (Push, Google)
    try {
      if (Capacitor.isNativePlatform()) {
        // Safe Push Cleanup
        try {
          await PushNotifications.removeAllListeners();
        } catch (e) {
          console.warn('[Auth] Push listener cleanup failed:', e);
        }

        // Safe Google Cleanup - Only if GoogleAuth was actually used in this session
        if (shouldCleanupGoogleAuth) {
          try {
            await GoogleAuth.signOut();
            console.log('[Auth] GoogleAuth signOut successful');
          } catch (e) {
            console.warn('[Auth] Google SignOut failed:', e);
          }
          // Reset the ref after cleanup
          googleAuthUsedRef.current = false;
        }
      } else if ('serviceWorker' in navigator && 'PushManager' in window) {
        // Web Push Cleanup
        try {
          const reg = await navigator.serviceWorker.ready;
          const sub = await reg.pushManager.getSubscription();
          if (sub) {
            const endpoint = sub.endpoint;
            // Best effort delete from DB
            await supabase.from('push_subscriptions').delete().eq('subscription->>endpoint', endpoint);
            await sub.unsubscribe();
          }
        } catch (e) {
          console.warn('[Auth] Web Push cleanup failed:', e);
        }
      }
      console.log('[Auth] Side-effect cleanups completed');
    } catch (error) {
      console.error('[Auth] Error during side-effect cleanups:', error);
    }

    // 4. Supabase SignOut (clears session and localStorage internally)
    try {
      await supabase.auth.signOut();
      console.log('[Auth] Supabase signOut called');
    } catch (error) {
      console.error('[Auth] Supabase signOut error:', error);
    }

    // 5. Hard clear localStorage for CareOS/Supabase keys as a safety measure
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.includes('sb-') || key.includes('careos-')) {
          localStorage.removeItem(key);
        }
      });
      console.log('[Auth] LocalStorage sanitized');
    } catch (e) {
      console.error('[Auth] Error sanitizing localStorage:', e);
    }

    // 6. Final cache clear
    queryClient.clear();
    console.log('[Auth] Query cache cleared');

    console.groupEnd();
    setIsLoggingOut(false); // Reset flag essentially

    // 7. Soft Redirect using Router
    navigate('/auth', { replace: true });
  };

  const hasRole = (role: AppRole) => roles.includes(role);

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      roles,
      subscription,
      isCheckingSubscription,
      hasCheckedSubscription,
      isRevalidatingSubscription,
      consecutiveNegativeChecks,

      isLoggingOut,
      signUp,
      signIn,
      signInWithGoogle,
      signInWithApple,
      signOut,
      hasRole,
      checkSubscription,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Non-throwing variant for optional/telemetry consumers that may render
 * outside (or before) the provider — e.g. during HMR module duplication.
 */
export function useOptionalAuth() {
  return useContext(AuthContext);
}

